import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  Button, 
  Card, 
  CardHeader, 
  CardFooter,
  Dropdown,
  DropdownItem,
  FileListModal,
  AlertDialog
} from '../ui';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../ui/Modal';
import { TablePresets } from '../tables';
import { 
  exportToPDF,
  exportToExcel,
  exportToCSV,
  exportToJSON
} from '../exports';
import { getLabels } from './labels';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/apiConfig';
import { mutate } from 'swr';
import { CogIcon, SpinnerIcon, DownloadIcon, PlusIcon, ArrowLeftIcon, ArrowRightIcon, PrinterIcon, DocumentTextIcon, PaperClipIcon } from '../ui/Icons';

// Extracted Components
import FilterSection from './FilterSection';
import TableSection from './TableSection';
import SplitViewPanel from './SplitViewPanel';
import FormDrawer from './FormDrawer';
import ViewModal from './ViewModal';
import DeleteModal from './DeleteModal';
import DuplicateConfirmModal from './DuplicateConfirmModal';
import { exportComplaintToPdf } from '../../modules/complaint/utils/exportComplaintToPdf';
import { exportRiskAssessmentToPdf } from '../../modules/risk-assessment/utils/exportRiskAssessmentToPdf';
import { exportRiskAssessmentToExcel } from '../../modules/risk-assessment/utils/exportRiskAssessmentToExcel';
import { exportDeviationToPdf } from '../../modules/deviation/utils/exportDeviationToPdf';
import { exportProductStatusChangeToPdf } from '../../modules/product-status-change/utils/exportProductStatusChangeToPdf';
import { exportReturnedProductInspectionToPdf } from '../../modules/returned-product-inspection/utils/exportReturnedProductInspectionToPdf';
import { exportChangeRequestToPdf } from '../../modules/change-request/utils/exportChangeRequestToPdf';

// Custom Hooks
import { useGenericFormState } from '../../hooks/useGenericFormState';
import { useFilterState } from '../../hooks/useFilterState';
import { useFlexibleList } from '../../hooks/useSWR';
import { useBulkCreateFileAttachments, useDeleteMultipleFileAttachments } from '../../hooks/useFileAttachments';

// Utils are imported in individual components

/**
 * GenericFormsPage - REFACTORED VERSION
 * Complete self-contained CRUD interface with extracted components
 */
const GenericFormsPage = ({ config }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const {
    entityName: configEntityName = 'Record',
    fields = [],
    tableColumns = [],
    exportFileName = 'data',
    onSave,
    onDelete
  } = config;
  
  // Use config.entityName if available, otherwise fall back to configEntityName
  // This ensures we always use the correct entity name from config
  const entityName = config.entityName || configEntityName;
  const hideListWhenFormOpen = config.ui?.hideListWhenFormOpen === true;

  // Get labels configuration
  const labels = getLabels(entityName, config.labels || {});

  // Get custom SplitViewPanel component if specified
  const SplitViewComponent = config.customSplitViewPanel?.enabled && config.customSplitViewPanel?.component
    ? config.customSplitViewPanel.component
    : SplitViewPanel;

  // Custom hooks for state management
  const {
    // Data state
    data,
    setData,
    searchTerm,
    setSearchTerm,
    
    // Pagination state
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    handlePreviousPage,
    handleNextPage,
    resetToFirstPage,
    
    // Table state
    tableDensity,
    tableVariant,
    showTablePresets,
    setShowTablePresets,
    tableConfig,
    visibleColumns,
    boardLookupState,
    setBoardLookupState,
    
    // Modal/Form state
    isFormOpen,
    setIsFormOpen,
    isViewModalOpen,
    setIsViewModalOpen,
    isSplitViewOpen,
    setIsSplitViewOpen,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    selectedRecord,
    setSelectedRecord,
    formMode,
    setFormMode,
    formData,
    setFormData,
    isLoading,
    setIsLoading,
    formErrors,
    setFormErrors,
    isFormValid,
    exportLoading,
    setExportLoading,
    formNotifications,
    setFormNotifications,
    
    // File attachment state
    fileListModalOpen,
    setFileListModalOpen,
    selectedRecordFiles,
    setSelectedRecordFiles,
    selectedRecordTitle,
    setSelectedRecordTitle,
    filesToDelete,
    setFilesToDelete,
    
    // Handlers
    handleDensityChange,
    handleTableConfigChange,
    handleApplyTableConfig,
    handleApplyVisibleColumns,
    addFormNotification,
    clearFormNotifications
  } = useGenericFormState(config);

  // Duplicate modal state
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [selectedRecordForDuplicate, setSelectedRecordForDuplicate] = useState(null);
  const [alertState, setAlertState] = useState({ show: false, message: '', type: 'error' });
  const [pendingEditRecordId, setPendingEditRecordId] = useState(null);
  const [todoTasksModalOpen, setTodoTasksModalOpen] = useState(false);
  const [selectedTodoTasks, setSelectedTodoTasks] = useState([]);
  const [selectedTodoTaskTitle, setSelectedTodoTaskTitle] = useState('');

  const closeFormAndNotify = React.useCallback(() => {
    setIsFormOpen(false);
    window.dispatchEvent(
      new CustomEvent('generic:formClosed', {
        detail: { entityName },
      }),
    );
  }, [setIsFormOpen, entityName]);
  
  // HTML preview modal state
  const [htmlPreviewRecord, setHtmlPreviewRecord] = useState(null);

  // Print modal state

  // Get custom Form components if specified (must be after formMode is defined)
  const CustomFormComponent = config.customFormPanel?.enabled 
    ? (formMode === 'add' 
        ? (config.customFormPanel?.addComponent || config.customFormPanel?.component)
        : (config.customFormPanel?.editComponent || config.customFormPanel?.component))
    : null;

  // Filter state hook
  const {
    filtersVisible,
    setFiltersVisible,
    unifiedFilters,
    setUnifiedFilters,
    activeFilterTab,
    setActiveFilterTab,
    savedFilterPresets,
    setSavedFilterPresets,
    showFilterPresets,
    setShowFilterPresets,
    handleApplyFilterPreset,
    refreshFilterPresets,
    clearAllFilters
  } = useFilterState(entityName, config);

  // SWR-powered board lookup
  const shouldLoadBoard = tableVariant === 'board' && !!config.ui?.boardLookupEndpoint;
  const { data: boardValues } = useFlexibleList(
    shouldLoadBoard ? config.ui.boardLookupEndpoint : null
  );

  React.useEffect(() => {
    if (!shouldLoadBoard) return;
    const idField = config.ui?.boardLookupIdField || 'id';
    const nameField = config.ui?.boardLookupNameField || 'name';
    const map = {};
    (boardValues || []).forEach(item => {
      const k = item?.[idField];
      const label = item?.[nameField];
      if (k !== undefined && k !== null) map[k] = label ?? String(k);
    });
    setBoardLookupState(map);
  }, [shouldLoadBoard, boardValues, config.ui?.boardLookupIdField, config.ui?.boardLookupNameField, setBoardLookupState]);

  // File attachment hooks
  const { bulkCreateFileAttachments } = useBulkCreateFileAttachments();
  const { deleteMultipleFileAttachments } = useDeleteMultipleFileAttachments();

  // Filter and search logic
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    
    // Enhance data with fileAttachmentCount if missing
    const enhancedData = data.map(item => {
      if (!item) return item;
      const enhanced = { ...item };
      
      // Calculate fileAttachmentCount if not provided by backend
      if (enhanced.fileAttachmentCount === undefined || enhanced.fileAttachmentCount === null) {
        // First try to count from fileAttachments array if available
        const fileAttachments = Array.isArray(enhanced.fileAttachments) ? enhanced.fileAttachments : [];
        if (fileAttachments.length > 0) {
          const activeFiles = fileAttachments.filter(file => !file.isDeleted);
          enhanced.fileAttachmentCount = activeFiles.length;
        } else {
          // If fileAttachments array is empty, set to 0 (will be fetched separately if needed)
          // The backend should provide fileAttachmentCount, but if not, we'll show 0
          // until files are loaded
          enhanced.fileAttachmentCount = 0;
        }
      } else {
        // Ensure it's a number
        enhanced.fileAttachmentCount = parseInt(enhanced.fileAttachmentCount) || 0;
      }
      
      // Debug: Log if fileAttachmentCount is 0 but we expect files
      if (config.features?.fileAttachments && enhanced.id && enhanced.fileAttachmentCount === 0) {
        // This will help identify if the backend isn't providing the count
        // We can add logic later to fetch counts separately if needed
      }
      
      return enhanced;
    });
    
    const filtered = enhancedData.filter(item => {
      if (!item) return false;
      
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = Object.values(item).some(value => 
          String(value || '').toLowerCase().includes(searchLower)
        );
        if (!matchesSearch) return false;
      }
      
      // Unified filters
      for (const [filterKey, filterConfig] of Object.entries(unifiedFilters)) {
        const itemValue = item[filterKey];
        
        if (filterConfig.type === 'select') {
          if (filterConfig.value && itemValue !== filterConfig.value) return false;
        } else if (filterConfig.type === 'multiSelect') {
          if (filterConfig.values && filterConfig.values.length > 0) {
            const field = fields.find(f => f.name === filterKey);
            let checkValue = itemValue;
            
            if (field?.lookupEndpoint) {
              if (field?.table?.displayField) {
                checkValue = item[field.table.displayField];
              } else {
                const sourceField = filterKey.replace('Id', '');
                const obj = item[sourceField];
                if (obj && typeof obj === 'object') {
                  checkValue = obj.name || obj.typeName || obj.statusName || obj.title || 'Unknown';
                }
              }
            }
            
            if (!filterConfig.values.includes(checkValue)) return false;
          }
        } else if (filterConfig.type === 'dateRange') {
          if (filterConfig.from || filterConfig.to || filterConfig.preset) {
            let itemDate;
            if (itemValue instanceof Date) {
              itemDate = itemValue;
            } else if (typeof itemValue === 'string') {
              itemDate = new Date(itemValue);
            } else {
              continue;
            }
            
            if (isNaN(itemDate.getTime())) continue;
            
            const itemDateOnly = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate());
            
            if (filterConfig.from) {
              const fromDate = new Date(filterConfig.from);
              const fromDateOnly = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
              if (itemDateOnly < fromDateOnly) return false;
            }
            
            if (filterConfig.to) {
              const toDate = new Date(filterConfig.to);
              const toDateOnly = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
              if (itemDateOnly > toDateOnly) return false;
            }
          }
        } else if (filterConfig.type === 'numericRange') {
          if (filterConfig.min !== undefined || filterConfig.max !== undefined) {
            const numValue = parseFloat(itemValue);
            if (isNaN(numValue)) continue;
            
            if (filterConfig.min !== undefined && numValue < filterConfig.min) return false;
            if (filterConfig.max !== undefined && numValue > filterConfig.max) return false;
          }
        } else if (filterConfig.type === 'boolean') {
          if (filterConfig.value !== undefined) {
            const itemBoolValue = Boolean(itemValue);
            if (itemBoolValue !== filterConfig.value) return false;
          }
        } else if (filterConfig.type === 'text') {
          if (filterConfig.value) {
            const itemStringValue = String(itemValue || '').toLowerCase();
            const searchValue = String(filterConfig.value).toLowerCase();
            if (!itemStringValue.includes(searchValue)) return false;
          }
        }
      }
      
      return true;
    });
    
    return filtered;
  }, [data, searchTerm, unifiedFilters, fields]);

  const sortedData = filteredData;

  // Pagination calculations
  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageData = sortedData.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    resetToFirstPage();
  }, [searchTerm, unifiedFilters, pageSize, resetToFirstPage]);

  // Get visible columns (Document Reference always last when present)
  const getVisibleColumns = () => {
    const cols = tableColumns.filter((col) => visibleColumns[col.key]);
    const referenceIdx = cols.findIndex(
      (col) =>
        col.key === 'referenceDocument' || col.originalField === 'referenceDocument',
    );
    if (referenceIdx >= 0 && referenceIdx !== cols.length - 1) {
      const [referenceCol] = cols.splice(referenceIdx, 1);
      cols.push(referenceCol);
    }
    return cols;
  };

  // Real-time field validation using Yup (if available)
  const validateField = async (fieldName, value) => {
    
    if (config.validateField && typeof config.validateField === 'function') {
      try {
        const result = await config.validateField(fieldName, value, formData, formMode);
        // If custom validation returns a result (even null), use it and skip fallback
        return result;
      } catch (error) {
        console.error(`❌ Custom validation error for ${fieldName}:`, error);
        return `Validation error: ${error.message}`;
      }
    }
    
    // Fallback validation
    const field = fields.find(f => f.name === fieldName);
    if (!field) return null;

    const isEmptyValue =
      value === undefined ||
      value === null ||
      value === '' ||
      (typeof value === 'string' && value.trim() === '');

    const evaluateDependsOn = () => {
      if (!field.dependsOn) return false;
      const { field: dependsOnField, value: dependsOnValue } = field.dependsOn;
      if (typeof dependsOnValue === 'function') {
        return !!dependsOnValue(formData, config?.lookupData || {});
      }
      return formData?.[dependsOnField] === dependsOnValue;
    };

    // Always-required fields
    if (
      (field.required === true || field.validation?.required === true) &&
      field.required !== 'conditional' &&
      isEmptyValue
    ) {
      return `${field.label} is required`;
    }

    // Conditionally required when dependsOn is met (e.g. closed status)
    if (field.required === 'conditional' && evaluateDependsOn() && isEmptyValue) {
      return `${field.label} is required`;
    }
    
    if (isEmptyValue) return null;
    if (field.type === 'fileUpload') return null;
    
    // Apply common rules from field.validation
    const rules = field.validation || {};
    const fail = (defaultMsg) => (rules.message ? rules.message : defaultMsg);
    
    if (typeof value === 'string') {
      if (rules.maxLength && value.length > rules.maxLength) {
        return fail(`${field.label} must not exceed ${rules.maxLength} characters`);
      }
      if (rules.minLength && value.length < rules.minLength) {
        return fail(`${field.label} must be at least ${rules.minLength} characters`);
      }
      if (rules.pattern) {
        try {
          const re = rules.pattern instanceof RegExp ? rules.pattern : new RegExp(rules.pattern);
          if (!re.test(value)) {
            return fail(`${field.label} has invalid format`);
          }
        } catch (_) {
          // Ignore invalid regex config
        }
      }
      if (rules.email) {
        const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRe.test(value)) {
          return fail('Please enter a valid email address');
        }
      }
    }
    
    const asNumber = Number(value);
    if (field.type === 'number' || rules.integer || rules.number || rules.min !== undefined || rules.max !== undefined) {
      if (Number.isNaN(asNumber)) {
        return fail(`${field.label} must be a valid number`);
      }
      if (rules.integer && !Number.isInteger(asNumber)) {
        return fail(`${field.label} must be an integer`);
      }
      if (rules.min !== undefined && asNumber < Number(rules.min)) {
        return fail(`${field.label} must be at least ${rules.min}`);
      }
      if (rules.max !== undefined && asNumber > Number(rules.max)) {
        return fail(`${field.label} must be at most ${rules.max}`);
      }
    }
    
    if (field.type === 'date') {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) {
        return fail(`${field.label} must be a valid date`);
      }
    }
    
    return null;
  };

  // Event handlers
  const handleAdd = () => {
    // Check if there's a custom handleAdd function in config
    if (config.customHandleAdd && typeof config.customHandleAdd === 'function') {
      config.customHandleAdd();
      return;
    }
    
    // Default behavior
    setSelectedRecord(null);
    setFormMode('add');
    
    // Initialize form data with field defaults and special field types
    const initialFormData = {};
    fields.forEach(field => {
      if (field.defaultValue !== undefined) {
        initialFormData[field.name] =
          typeof field.defaultValue === 'function'
            ? field.defaultValue({ formMode: 'add' })
            : field.defaultValue;
      }
      if (field.type === 'fileUpload') {
        initialFormData[field.name] = [];
      }
    });
    
    setFormData(initialFormData);
    setFormErrors({});
    clearFormNotifications();
    setIsFormOpen(true);
  };

  // Listen for custom event to open form with pre-selected type
  React.useEffect(() => {
    const handleOpenFormWithType = (event) => {
      const { typeId, typeData } = event.detail;
      setSelectedRecord(null);
      setFormMode('add');
      
      // Initialize form data with selected type
      const initialFormData = {
        complaintTypeId: typeId,
      };
      
      fields.forEach(field => {
        if (field.type === 'fileUpload') {
          initialFormData[field.name] = [];
        }
      });
      
      setFormData(initialFormData);
      setFormErrors({});
      clearFormNotifications();
      setIsFormOpen(true);
      
      // Clear the pending type
      if (window.__pendingComplaintTypeId) {
        delete window.__pendingComplaintTypeId;
      }
    };

    window.addEventListener('complaint:openFormWithType', handleOpenFormWithType);
    
    return () => {
      window.removeEventListener('complaint:openFormWithType', handleOpenFormWithType);
    };
  }, [fields, setFormMode, setFormData, setFormErrors, clearFormNotifications, setIsFormOpen]);

  const handleEdit = (record) => {
    // Check if editing is allowed for this record
    if (config.canEdit && !config.canEdit(record)) {
      const status = record?.status?.name || record?.status;
      const isCancelled = status?.toLowerCase() === 'cancelled';
      setAlertState({
        show: true,
        message: isCancelled 
          ? 'This approval request has been cancelled and cannot be edited.' 
          : 'This record cannot be edited in its current status.',
        type: 'warning'
      });
      return;
    }
    
    // Normalize form data - convert lookupEndpoint fields from objects to IDs
    const normalizedFormData = { ...record };
    fields.forEach(field => {
      if (field.lookupEndpoint && (field.type === 'select' || field.type === 'multiSelect')) {
        const fieldValue = normalizedFormData[field.name];
        // Handle array of objects (for multiSelect with many-to-many relations)
        if (Array.isArray(fieldValue) && fieldValue.length > 0 && typeof fieldValue[0] === 'object') {
          // Transform array of objects to array of IDs
          normalizedFormData[field.name] = fieldValue.map(item => item.id || item.value || item);
        } else if (fieldValue && typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
          // If value is a single object, extract the ID
          normalizedFormData[field.name] = fieldValue.id || fieldValue.value || fieldValue;
        }
      }
      // Locked lookup fields always use the resolved locked value (e.g. Procedures module)
      if (field.lockedLookupName && field.lockedValue != null) {
        normalizedFormData[field.name] = field.lockedValue;
      } else if (field.lockedLookupName && field.defaultValue != null) {
        normalizedFormData[field.name] = field.defaultValue;
      }
      // Special handling for rootCauses -> rootCauseIds transformation
      if (field.name === 'rootCauseIds' && normalizedFormData.rootCauses) {
        if (Array.isArray(normalizedFormData.rootCauses) && normalizedFormData.rootCauses.length > 0) {
          normalizedFormData.rootCauseIds = normalizedFormData.rootCauses.map(rc => rc.id);
        } else {
          normalizedFormData.rootCauseIds = [];
        }
      }
      // Special handling for finalDispositionId - extract ID from lookup object
      if (field.name === 'finalDispositionId' && normalizedFormData.finalDisposition) {
        // If finalDisposition is an object, extract the ID
        if (typeof normalizedFormData.finalDisposition === 'object' && normalizedFormData.finalDisposition.id) {
          normalizedFormData.finalDispositionId = normalizedFormData.finalDisposition.id;
        }
      }
    });
    
    setSelectedRecord(record);
    setFormMode('edit');
    setFormData(normalizedFormData);
    setFormErrors({});
    clearFormNotifications();
    setIsFormOpen(true);
  };

  // Keep locked lookup fields (e.g. Procedures module) synced once options resolve
  React.useEffect(() => {
    if (!isFormOpen) return;
    const updates = {};
    fields.forEach((field) => {
      if (!field.lockedLookupName) return;
      const lockedValue = field.lockedValue ?? field.defaultValue;
      if (lockedValue == null) return;
      if (String(formData?.[field.name] ?? '') !== String(lockedValue)) {
        updates[field.name] = lockedValue;
      }
    });
    if (Object.keys(updates).length > 0) {
      setFormData((prev) => ({ ...prev, ...updates }));
    }
  }, [isFormOpen, fields, formData, setFormData]);

  React.useEffect(() => {
    const onOpenEditById = (event) => {
      const targetEntity = event?.detail?.entityName;
      const targetId = event?.detail?.id;
      if (!targetEntity || targetId == null) return;
      if (String(targetEntity).toLowerCase() !== String(entityName).toLowerCase()) return;
      setPendingEditRecordId(Number(targetId));
    };

    window.addEventListener('generic:openEditById', onOpenEditById);
    return () => window.removeEventListener('generic:openEditById', onOpenEditById);
  }, [entityName]);

  React.useEffect(() => {
    const onOpenAddByEntity = (event) => {
      const targetEntity = event?.detail?.entityName;
      if (!targetEntity) return;
      if (String(targetEntity).toLowerCase() !== String(entityName).toLowerCase()) return;
      handleAdd();
    };

    window.addEventListener('generic:openAddByEntity', onOpenAddByEntity);
    return () => window.removeEventListener('generic:openAddByEntity', onOpenAddByEntity);
  }, [entityName, handleAdd]);

  React.useEffect(() => {
    if (pendingEditRecordId == null) return;
    const record = (data || []).find((item) => Number(item?.id) === Number(pendingEditRecordId));
    if (!record) return;
    handleEdit(record);
    setPendingEditRecordId(null);
  }, [pendingEditRecordId, data]);

  const handleView = (record) => {
    if (config.ui?.splitView) {
      handleSplitViewSelect(record);
      return;
    }
    setSelectedRecord(record);
    setIsViewModalOpen(true);
  };

  const handleDuplicate = (record) => {
    // Open the duplicate confirmation modal
    setSelectedRecordForDuplicate(record);
    setIsDuplicateModalOpen(true);
  };

  const handleDuplicateConfirm = async () => {
    if (!selectedRecordForDuplicate) {
      return;
    }

    try {
      setIsLoading(true);
      addFormNotification(`Duplicating ${entityName}...`, 'info');
      
      let duplicatedRecord;
      
      // Check if there's a custom duplicate handler
      if (config.customHandlers?.onDuplicate && typeof config.customHandlers.onDuplicate === 'function') {
        // Use custom duplicate handler (e.g., for risk assessment that doesn't have backend endpoint)
        duplicatedRecord = await config.customHandlers.onDuplicate(selectedRecordForDuplicate.id);
      } else {
        // Use backend duplicate endpoint (default behavior)
        const duplicateEndpoint = config.api?.endpoints?.duplicate?.(selectedRecordForDuplicate.id);
        if (!duplicateEndpoint) {
          throw new Error('Duplicate endpoint not configured');
        }
        
        const response = await fetch(`${API_BASE_URL}${config.api.baseEndpoint}${duplicateEndpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {}),
            ...(localStorage.getItem('userId') ? { 'user-id': localStorage.getItem('userId') } : {})
          },
          body: JSON.stringify({}) // Optional: can pass { code: 'new-code' } if needed
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Failed to duplicate record' }));
          throw new Error(errorData.message || 'Failed to duplicate record');
        }
        
        duplicatedRecord = await response.json();
      }
      
      // Refresh the data
      if (config.api?.swrInvalidation?.mainList) {
        mutate(config.api.swrInvalidation.mainList);
      } else {
        mutate(config.api.baseEndpoint);
      }
      
      addFormNotification(`${entityName} duplicated successfully!`, 'success');
      
      // Close the modal
      setIsDuplicateModalOpen(false);
      setSelectedRecordForDuplicate(null);
      
      // Open the duplicated record in edit mode
      setTimeout(() => {
        handleEdit(duplicatedRecord);
      }, 500);
      
    } catch (error) {
      console.error('Duplicate error:', error);
      addFormNotification(
        `Failed to duplicate ${entityName}: ${error.message}`,
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Export complaint to PDF handler
  const handlePrintComplaint = async (complaintId, includeAttachments = false) => {
    try {
      setIsLoading(true);
      addFormNotification(includeAttachments ? 'Generating PDF report with attachments...' : 'Generating PDF report...', 'info');
      
      // Fetch full complaint data with relations
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      
      const response = await fetch(`${API_BASE_URL}/complaint/${complaintId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(userId ? { 'user-id': userId } : {})
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch complaint data');
      }

      const complaint = await response.json();
      
      // Fetch related actions and actions taken
      const [actionsTakenRes, actionsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/complaint-action-taken/complaint/${complaintId}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userId ? { 'user-id': userId } : {})
          }
        }).catch(() => ({ ok: false, json: () => [] })),
        fetch(`${API_BASE_URL}/complaint-action/complaint/${complaintId}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userId ? { 'user-id': userId } : {})
          }
        }).catch(() => ({ ok: false, json: () => [] }))
      ]);

      const actionsTaken = actionsTakenRes.ok ? await actionsTakenRes.json() : [];
      const actions = actionsRes.ok ? await actionsRes.json() : [];

      // Merge the data
      const fullComplaint = {
        ...complaint,
        complaintActionTaken: actionsTaken,
        complaintActions: actions,
        includeAttachments: includeAttachments
      };

      // Export to PDF
      const result = await exportComplaintToPdf(fullComplaint, includeAttachments);
      
      if (result?.missingFilesCount > 0) {
        addFormNotification(
          `PDF report generated successfully! Note: ${result.missingFilesCount} file(s) could not be included (may be deleted or missing).`, 
          'warning'
        );
      } else {
        addFormNotification('PDF report generated successfully!', 'success');
      }
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      addFormNotification(`Failed to generate PDF: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Export deviation to PDF handler
  const handlePrintDeviation = async (deviationId, includeAttachments = false) => {
    try {
      setIsLoading(true);
      addFormNotification(includeAttachments ? 'Generating PDF report with attachments...' : 'Generating PDF report...', 'info');
      
      // Fetch full deviation data with relations
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      
      const response = await fetch(`${API_BASE_URL}/deviation/${deviationId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(userId ? { 'user-id': userId } : {})
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch deviation data');
      }

      const deviation = await response.json();
      
      // Fetch related actions
      const actionsRes = await fetch(`${API_BASE_URL}/deviation-action/by-deviation/${deviationId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(userId ? { 'user-id': userId } : {})
        }
      }).catch(() => ({ ok: false, json: () => [] }));

      const actions = actionsRes.ok ? await actionsRes.json() : [];

      // Merge the data
      const fullDeviation = {
        ...deviation,
        deviationActions: actions,
        includeAttachments: includeAttachments
      };

      // Export to PDF
      const result = await exportDeviationToPdf(fullDeviation, includeAttachments);
      
      if (result?.missingFilesCount > 0) {
        addFormNotification(
          `PDF report generated successfully! Note: ${result.missingFilesCount} file(s) could not be included (may be deleted or missing).`, 
          'warning'
        );
      } else {
        addFormNotification('PDF report generated successfully!', 'success');
      }
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      addFormNotification(`Failed to generate PDF: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Export product status change to PDF handler
  const handleExportProductStatusChangeToPdf = async (productStatusChangeId, includeAttachments = false) => {
    try {
      setIsLoading(true);
      addFormNotification('Generating PDF report...', 'info');
      
      // Fetch full product status change data with relations
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      
      const response = await fetch(`${API_BASE_URL}/product-status-change/${productStatusChangeId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(userId ? { 'user-id': userId } : {})
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch product status change data');
      }

      const fullProductStatusChange = await response.json();

      // Export to PDF
      const result = await exportProductStatusChangeToPdf(fullProductStatusChange, includeAttachments);
      
      addFormNotification('PDF report generated successfully', 'success');
      return result;
    } catch (error) {
      console.error('Error exporting product status change to PDF:', error);
      addFormNotification('Failed to generate PDF report', 'error');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Export returned product inspection to PDF handler
  const handleExportReturnedProductInspectionToPdf = async (
    recordId,
    includeAttachments = false,
  ) => {
    try {
      setIsLoading(true);
      addFormNotification(
        includeAttachments
          ? 'Generating PDF report with attachments...'
          : 'Generating PDF report...',
        'info',
      );

      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');

      const response = await fetch(
        `${API_BASE_URL}/returned-product-inspection/${recordId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userId ? { 'user-id': userId } : {}),
          },
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch returned product inspection data');
      }

      const fullRecord = await response.json();
      const result = await exportReturnedProductInspectionToPdf(
        fullRecord,
        includeAttachments,
      );

      if (result?.missingFilesCount > 0) {
        addFormNotification(
          `PDF report generated successfully! Note: ${result.missingFilesCount} file(s) could not be included (may be deleted or missing).`,
          'warning',
        );
      } else {
        addFormNotification('PDF report generated successfully!', 'success');
      }
      return result;
    } catch (error) {
      console.error('Error exporting returned product inspection to PDF:', error);
      addFormNotification(`Failed to generate PDF: ${error.message}`, 'error');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Export Change Request / Change Control to PDF handler
  const handlePrintChangeRequest = async (
    recordId,
    includeAttachments = false,
  ) => {
    try {
      setIsLoading(true);
      addFormNotification(
        includeAttachments
          ? 'Generating PDF report with attachments...'
          : 'Generating PDF report...',
        'info',
      );

      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');

      const response = await fetch(
        `${API_BASE_URL}/change-request/${recordId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userId ? { 'user-id': userId } : {}),
          },
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch change request data');
      }

      const fullRecord = await response.json();
      const result = await exportChangeRequestToPdf(
        fullRecord,
        includeAttachments,
      );

      if (result?.missingFilesCount > 0) {
        addFormNotification(
          `PDF report generated successfully! Note: ${result.missingFilesCount} file(s) could not be included (may be deleted or missing).`,
          'warning',
        );
      } else {
        addFormNotification('PDF report generated successfully!', 'success');
      }
      return result;
    } catch (error) {
      console.error('Error exporting change request to PDF:', error);
      addFormNotification(`Failed to generate PDF: ${error.message}`, 'error');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Export CAPA Effectiveness Check to PDF handler
  const handlePrintCapaEffectiveness = async (
    recordId,
    includeAttachments = false,
  ) => {
    try {
      setIsLoading(true);
      addFormNotification(
        includeAttachments
          ? 'Generating PDF report with attachments...'
          : 'Generating PDF report...',
        'info',
      );

      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');

      const response = await fetch(
        `${API_BASE_URL}/capa-effectiveness-check/${recordId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userId ? { 'user-id': userId } : {}),
          },
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch CAPA effectiveness check data');
      }

      const fullRecord = await response.json();
      const { exportCapaEffectivenessToPdf: exportPdf } = await import(
        '../../modules/capa-effectiveness/utils/exportCapaEffectivenessToPdf'
      );
      const result = await exportPdf(fullRecord, includeAttachments);

      if (result?.missingFilesCount > 0) {
        addFormNotification(
          `PDF report generated successfully! Note: ${result.missingFilesCount} file(s) could not be included (may be deleted or missing).`,
          'warning',
        );
      } else {
        addFormNotification('PDF report generated successfully!', 'success');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      addFormNotification(`Failed to generate PDF: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Export risk assessment to PDF handler
  const handlePrintRiskAssessment = async (riskAssessmentId, includeAttachments = false) => {
    try {
      setIsLoading(true);
      addFormNotification(includeAttachments ? 'Generating PDF report with attachments...' : 'Generating PDF report...', 'info');
      
      // Fetch full risk assessment data with relations
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      
      const response = await fetch(`${API_BASE_URL}/risk-assessment/${riskAssessmentId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(userId ? { 'user-id': userId } : {})
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch risk assessment data');
      }

      const riskAssessment = await response.json();
      
      // Fetch related timeline entries (actions are already included in the main response)
      const timelineRes = await fetch(`${API_BASE_URL}/risk-assessment-timeline-entry/by-risk-assessment/${riskAssessmentId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(userId ? { 'user-id': userId } : {})
        }
      }).catch(() => ({ ok: false, json: () => [] }));

      const timelineEntries = timelineRes.ok ? await timelineRes.json() : [];

      // Merge the data
      const fullRiskAssessment = {
        ...riskAssessment,
        riskAssessmentActions: riskAssessment.riskAssessmentActions || [],
        riskAssessmentTimeLineEntries: timelineEntries
      };

      // Export to PDF
      await exportRiskAssessmentToPdf(fullRiskAssessment, includeAttachments);
      
      addFormNotification('PDF report generated successfully!', 'success');
    } catch (error) {
      console.error('Error generating PDF:', error);
      addFormNotification(`Failed to generate PDF: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Export risk assessment to Excel handler
  const handleExportRiskAssessmentToExcel = async (riskAssessmentId) => {
    try {
      setIsLoading(true);
      addFormNotification('Generating Excel report...', 'info');
      
      // Fetch full risk assessment data with relations
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      
      const response = await fetch(`${API_BASE_URL}/risk-assessment/${riskAssessmentId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(userId ? { 'user-id': userId } : {})
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch risk assessment data');
      }

      const riskAssessment = await response.json();
      
      // Fetch related timeline entries (actions are already included in the main response)
      const timelineRes = await fetch(`${API_BASE_URL}/risk-assessment-timeline-entry/by-risk-assessment/${riskAssessmentId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(userId ? { 'user-id': userId } : {})
        }
      }).catch(() => ({ ok: false, json: () => [] }));

      const timelineEntries = timelineRes.ok ? await timelineRes.json() : [];

      // Merge the data
      const fullRiskAssessment = {
        ...riskAssessment,
        riskAssessmentActions: riskAssessment.riskAssessmentActions || [],
        riskAssessmentTimeLineEntries: timelineEntries
      };

      // Export to Excel
      await exportRiskAssessmentToExcel(fullRiskAssessment);
      
      addFormNotification('Excel report generated successfully!', 'success');
    } catch (error) {
      console.error('Error generating Excel report:', error);
      addFormNotification(`Failed to generate Excel report: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const resolveFileAttachmentEntityId = (record) => {
    const fieldName = config.features?.fileAttachmentEntityIdField;
    if (fieldName && record?.[fieldName]) {
      return record[fieldName];
    }
    return record?.id;
  };

  const handleSplitViewSelect = async (record) => {
    setSelectedRecord(record);
    setIsSplitViewOpen(true);
    
    // Check if file attachments are enabled and record has files
    const hasFileAttachments = config.features?.fileAttachments === true;
    const fileCount = parseInt(record.fileAttachmentCount) || 0;
    const attachmentEntityId = resolveFileAttachmentEntityId(record);
    const useAlternateEntityField = Boolean(config.features?.fileAttachmentEntityIdField);
    const shouldFetchFiles =
      hasFileAttachments &&
      config.entityTypeId &&
      attachmentEntityId &&
      record?.canView !== false &&
      (fileCount > 0 || useAlternateEntityField);
    
    if (shouldFetchFiles) {
      try {

        // Try module-specific endpoint first, then fall back to generic
        const moduleEndpoint = config.api?.endpoints?.fileAttachments?.(record.id);
        const apiUrl = moduleEndpoint 
          ? `${API_BASE_URL}${config.api.baseEndpoint}${moduleEndpoint}`
          : `${API_BASE_URL}/file-attachment/entity/${config.entityTypeId}/${attachmentEntityId}`;

        const response = await fetch(apiUrl, {
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {})
          }
        });
        
        if (response.ok) {
          const files = await response.json();

          setSelectedRecordFiles(files);
        }
      } catch (error) {
        console.error('Error fetching files for split view:', error);
        setSelectedRecordFiles([]);
      }
    } else {

      setSelectedRecordFiles([]);
    }
  };

  const handleDelete = (record) => {
    // Check if delete is allowed for this record
    const canDelete = config.canDelete ? config.canDelete(record) : true;
    if (!canDelete) {
      addFormNotification(
        `Cannot delete ${entityName.toLowerCase()}: actions have been taken on this approval request.`,
        'error'
      );
      return;
    }
    setSelectedRecord(record);
    setIsDeleteModalOpen(true);
  };

  // Handle file count click
  const handleFileCountClick = async (record, count) => {
    if (record?.canView === false) {
      addFormNotification('Files are available after this procedure is published.', 'warning');
      return;
    }

    // If fileAttachments are already in the record (from my-approvals), use them directly
    if (record.fileAttachments && Array.isArray(record.fileAttachments) && record.fileAttachments.length > 0) {
      setSelectedRecordFiles(record.fileAttachments);
      setSelectedRecordTitle(`${entityName} - ${record.code || record.title || record.name || record.id}`);
      setFileListModalOpen(true);
      return;
    }

    const attachmentEntityId = resolveFileAttachmentEntityId(record);

    if (config.entityTypeId && attachmentEntityId) {
      try {

        
        const response = await fetch(API_ENDPOINTS.fileAttachment.byEntity(config.entityTypeId, attachmentEntityId), {
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {})
          }
        });
        

        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const files = await response.json();

        
        const key = `/file-attachment/entity/${config.entityTypeId}/${attachmentEntityId}`;
        mutate(key, files, false);
        
        // Always show modal, even if no files
        setSelectedRecordFiles(files || []);
        setSelectedRecordTitle(`${entityName} - ${record.code || record.title || record.name || record.id}`);
        setFileListModalOpen(true);

        
      } catch (error) {
        console.error('Error fetching files:', error);
        addFormNotification('Error loading files', 'error');
      }
    } else {

      addFormNotification('File attachments not configured for this entity', 'warning');
    }
  };

  const handleTodoTaskCountClick = (record, tasks) => {
    setSelectedTodoTasks(Array.isArray(tasks) ? tasks : []);
    setSelectedTodoTaskTitle(record?.subject || record?.description || `${entityName} #${record?.id || ''}`);
    setTodoTasksModalOpen(true);
  };

  // Default file upload handler
  const handleDefaultFileUpload = async (fileAttachments, recordId) => {
    try {
      // Add masterEntityTypeId to each file attachment if not already present
      const fileAttachmentsWithMasterId = fileAttachments.map(attachment => ({
        ...attachment,
        masterEntityTypeId: attachment.masterEntityTypeId || config.features?.masterEntityTypeId || 1
      }));
      
      await bulkCreateFileAttachments(fileAttachmentsWithMasterId, recordId);
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Force sync custom component data before validation/save
    // This ensures data from custom components (like ComplaintActionTakenIntegration) is in formData
    const isComplaintForm = entityName === 'Complaint' || config.entityName === 'Complaint';
    const isDeviationForm = entityName === 'Deviation' || config.entityName === 'Deviation';
    
    if (isComplaintForm) {
      if (typeof window !== 'undefined' && window.__syncComplaintActionTaken) {
        console.log('🔄 Force syncing complaintActionTaken before form submission');
        window.__syncComplaintActionTaken();
      }
      if (typeof window !== 'undefined' && window.__syncComplaintActions) {
        console.log('🔄 Force syncing complaintActions before form submission');
        window.__syncComplaintActions();
      }
      // Wait a bit for state to update
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    if (isDeviationForm) {
      if (typeof window !== 'undefined' && window.__syncDeviationActions) {
        console.log('🔄 Force syncing deviationActions before form submission');
        window.__syncDeviationActions();
      }
      if (typeof window !== 'undefined' && window.__syncDeviationProducts) {
        console.log('🔄 Force syncing deviationProducts before form submission');
        window.__syncDeviationProducts();
      }
      // Wait a bit for state to update
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Full form validation
    const validationErrors = {};
    let hasValidationErrors = false;
    
    for (const field of fields.filter(
      (f) =>
        f.type !== 'hidden' &&
        f.type !== 'fileUpload' &&
        (f.validation || f.required === true || f.required === 'conditional')
    )) {
      const value = formData[field.name];
      const error = await validateField(field.name, value);
      if (error) {
        validationErrors[field.name] = error;
        hasValidationErrors = true;
      }
    }
    
    setFormErrors(validationErrors);
    
    const allErrors = { ...formErrors, ...validationErrors };
    const hasErrors = Object.values(allErrors).some(error => error && error.trim() !== '');
    
    if (hasErrors || hasValidationErrors) {
      addFormNotification('Please fix the validation errors before saving.', 'error');
      // Ensure loading state is not set when validation fails early
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    clearFormNotifications();
    
    try {
      // Get the latest formData using a promise-based approach to ensure we have the synced data
      // First, force sync again to ensure latest data
      // Only sync complaint-specific data if this is a complaint form
      const isComplaintForm = entityName === 'Complaint' || config.entityName === 'Complaint';
      
      if (isComplaintForm) {
        if (typeof window !== 'undefined' && window.__syncComplaintActionTaken) {
          window.__syncComplaintActionTaken();
        }
        if (typeof window !== 'undefined' && window.__syncComplaintActions) {
          window.__syncComplaintActions();
        }
      }
      
      // Wait for state to update - use a longer delay to ensure React has processed the sync
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Get the latest formData - use the current formData state
      // The force sync above should have updated it
      const latestFormData = { ...formData };
      
      // Also try to get data directly from the sync function if available (only for complaint forms)
      if (isComplaintForm) {
        if (typeof window !== 'undefined' && window.__getComplaintActionTaken) {
          const syncData = window.__getComplaintActionTaken();
          if (syncData && syncData.localActionsTaken && Array.isArray(syncData.localActionsTaken)) {
            latestFormData.complaintActionTaken = syncData.localActionsTaken;
            console.log('🔄 Using directly synced complaintActionTaken:', latestFormData.complaintActionTaken);
          }
        }
        
        // Also try to get complaintActions data directly from the sync function if available
        if (typeof window !== 'undefined' && window.__getComplaintActions) {
          const syncData = window.__getComplaintActions();
          if (syncData && syncData.localActions && Array.isArray(syncData.localActions)) {
            latestFormData.complaintActions = syncData.localActions;
            console.log('🔄 Using directly synced complaintActions:', latestFormData.complaintActions);
          }
        }
      }
      
      // Debug: Log formData before save to check if complaintActionTaken is present
      console.log('💾 Form submission - formData keys:', Object.keys(latestFormData));
      console.log('💾 Form submission - complaintActionTaken:', latestFormData.complaintActionTaken);
      console.log('💾 Form submission - complaintActionTaken length:', latestFormData.complaintActionTaken?.length || 0);
      console.log('💾 Form submission - full formData:', JSON.stringify(latestFormData, null, 2));
      
      const fileAttachments = latestFormData.fileAttachments || [];
      let formDataWithoutFiles = { ...latestFormData };
      delete formDataWithoutFiles.fileAttachments;
      delete formDataWithoutFiles.fileAttachmentCount;
      
      // Apply transformData if provided in config
      if (config.transformData && typeof config.transformData === 'function') {
        formDataWithoutFiles = config.transformData(formDataWithoutFiles, formMode);
        console.log('💾 Form submission - after transformData:', JSON.stringify(formDataWithoutFiles, null, 2));
      }

      if (onSave) {
        console.log('🚀 Calling onSave with formDataWithoutFiles - complaintActionTaken:', formDataWithoutFiles.complaintActionTaken);
        let savedRecord;
        try {
          // Start from cleaned/transformed data and only copy over essential metadata (like id)
          const mergedFormData = { ...formDataWithoutFiles };
          
          // Preserve primary key for updates
          if (latestFormData.id !== undefined && latestFormData.id !== null) {
            mergedFormData.id = latestFormData.id;
          }

          savedRecord = await onSave(mergedFormData, formMode, data, setData);
        } catch (saveError) {
          // Reset loading immediately on save error
          setIsLoading(false);
          throw saveError; // Re-throw to be caught by outer catch block
        }
        
        if ((fileAttachments.length > 0 || filesToDelete.length > 0) && savedRecord && savedRecord.id) {
          try {
            if (fileAttachments.length > 0) {
              if (config.customHandlers?.onFileUpload) {
                await config.customHandlers.onFileUpload(fileAttachments, savedRecord.id);
              } else {
                await handleDefaultFileUpload(fileAttachments, savedRecord.id);
              }
            }
            
            const deletedCount = filesToDelete.length;
            if (filesToDelete.length > 0 && config.entityTypeId) {
              const fileIds = filesToDelete.map(file => Number(file.id));
              
              // Delete files
              await deleteMultipleFileAttachments(fileIds, config.entityTypeId, savedRecord.id);
              
              // Force invalidate and refetch the specific entity's file attachments cache
              // Wait for the refetch to complete before proceeding
              const fileAttachmentsKey = `/file-attachment/entity/${config.entityTypeId}/${savedRecord.id}`;
              
              // First, optimistically update the cache by removing deleted files
              mutate(fileAttachmentsKey, (currentData) => {
                if (!currentData) return currentData;
                return currentData.filter(file => !fileIds.includes(file.id));
              }, false);
              
              // Then revalidate to get fresh data from server and wait for it
              await mutate(fileAttachmentsKey, undefined, { revalidate: true });
              
              // Don't clear filesToDelete here - keep it until form closes to prevent flash
              // The component filters files based on filesToDelete, so keeping it ensures
              // deleted files stay hidden even if cache hasn't fully refreshed
            }
            
            const uploadMsg = fileAttachments.length > 0 ? ` with ${fileAttachments.length} file(s) uploaded` : '';
            const deleteMsg = deletedCount > 0 ? ` and ${deletedCount} file(s) deleted` : '';
            
            addFormNotification(
              `${entityName} ${formMode === 'add' ? 'created' : 'updated'} successfully${uploadMsg}${deleteMsg}!`, 
              'success'
            );

            // Invalidate SWR cache
            if (config.api?.swrInvalidation) {
              if (config.api.swrInvalidation.mainList) {
                mutate(config.api.swrInvalidation.mainList);
              }
              if (config.api.swrInvalidation.withFileAttachments) {
                mutate(config.api.swrInvalidation.withFileAttachments);
              }
              if (config.api.swrInvalidation.fileAttachments && config.entityTypeId) {
                const fileAttachmentsEndpoint = config.api.swrInvalidation.fileAttachments(config.entityTypeId);
                mutate(fileAttachmentsEndpoint);
              }
            } else if (config.apiEndpoint) {
              mutate(config.apiEndpoint);
            }
            
            // Invalidate entity-specific file attachments cache (ensure it's cleared)
            if (savedRecord && savedRecord.id && config.entityTypeId) {
              const fileAttachmentsKey = `/file-attachment/entity/${config.entityTypeId}/${savedRecord.id}`;
              mutate(fileAttachmentsKey, undefined, { revalidate: true });
            }
          } catch (fileError) {
            console.error('File operation error:', fileError);
            addFormNotification(
              `${entityName} saved but file operations failed: ${fileError.message}`, 
              'warning'
            );
          }
        } else {
        addFormNotification(
          `${entityName} ${formMode === 'add' ? 'created' : 'updated'} successfully!`, 
          'success'
        );
        }
        
        // Wait a bit longer if files were deleted to ensure cache is refreshed and UI updated
        const closeDelay = (fileAttachments.length > 0 || filesToDelete.length > 0) ? 2000 : 1500;
        
        setTimeout(() => {
          closeFormAndNotify();
          setFormData({});
          setFormErrors({});
          setFilesToDelete([]);
          clearFormNotifications();
        }, closeDelay);
      } else {
        // Default save logic
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (formMode === 'add') {
          const newId = Math.max(...data.map(item => item.id || 0)) + 1;
          setData(prev => [...prev, { ...formDataWithoutFiles, id: newId }]);
        } else {
          setData(prev => prev.map(item => 
            item.id === formData.id ? formDataWithoutFiles : item
          ));
        }
        
        addFormNotification(
          `${entityName} ${formMode === 'add' ? 'created' : 'updated'} successfully!`, 
          'success'
        );

        // Invalidate SWR cache
        if (config.api?.swrInvalidation) {
          if (config.api.swrInvalidation.mainList) {
            mutate(config.api.swrInvalidation.mainList);
          }
          if (config.api.swrInvalidation.withFileAttachments) {
            mutate(config.api.swrInvalidation.withFileAttachments);
          }
          if (config.api.swrInvalidation.fileAttachments && config.entityTypeId) {
            const fileAttachmentsEndpoint = config.api.swrInvalidation.fileAttachments(config.entityTypeId);
            mutate(fileAttachmentsEndpoint);
          }
        } else if (config.apiEndpoint) {
          mutate(config.apiEndpoint);
        }
        
        setTimeout(() => {
          closeFormAndNotify();
          setFormData({});
          setFormErrors({});
          setFilesToDelete([]);
          clearFormNotifications();
        }, 1500);
      }
    } catch (error) {
      console.error('Save error:', error);
      const payload = error?.info;
      let msg = error?.message || '';
      if (Array.isArray(payload?.details) && payload.details.length) {
        msg = payload.details.join(', ');
      }
      if (payload?.errorCode === 'P2002') {
        const tgt = Array.isArray(payload?.meta?.target)
          ? payload.meta.target.join(', ')
          : payload?.meta?.target;
        msg = `Unique constraint violation${tgt ? ` (${tgt})` : ''}`;
      }
      addFormNotification(
        msg || `Failed to ${formMode === 'add' ? 'create' : 'update'} ${entityName.toLowerCase()}. Please try again.`,
        'error'
      );
      // Ensure loading state is reset on error so user can try again
      setIsLoading(false);
    } finally {
      // Double-check that loading is always reset
      setIsLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsLoading(true);
    
    try {
      if (onDelete) {
        await onDelete(selectedRecord, data, setData);
      } else {
        await new Promise(resolve => setTimeout(resolve, 300));
        setData(prev => prev.filter(item => item.id !== selectedRecord.id));
      }
      
      // Invalidate SWR cache
      if (config.api?.swrInvalidation) {
        if (config.api.swrInvalidation.mainList) {
          mutate(config.api.swrInvalidation.mainList);
        }
        if (config.api.swrInvalidation.withFileAttachments) {
          mutate(config.api.swrInvalidation.withFileAttachments);
        }
        if (config.api.swrInvalidation.fileAttachments && config.entityTypeId) {
          const fileAttachmentsEndpoint = config.api.swrInvalidation.fileAttachments(config.entityTypeId);
          mutate(fileAttachmentsEndpoint);
        }
      } else if (config.apiEndpoint) {
        mutate(config.apiEndpoint);
      }
      
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error('Delete error:', error);
      const payload = error?.info;
      let msg = error?.message || 'Delete failed';
      if (payload?.errorCode === 'P2003') {
        const constraint = payload?.meta?.constraint;
        const model = payload?.meta?.modelName;
        msg = `Cannot delete ${model || entityName}: it is referenced by related records${constraint ? ` (${constraint})` : ''}.`;
      }
      addFormNotification(msg, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Export handlers
  const handleExportToCSV = async () => {
    setExportLoading('csv');
    try {
      await exportToCSV(sortedData, tableColumns, exportFileName);
    } catch (error) {
      console.error('CSV export failed:', error);
    }
    setExportLoading('');
  };

  const handleExportToJSON = async () => {
    setExportLoading('json');
    try {
      await exportToJSON(sortedData, exportFileName);
    } catch (error) {
      console.error('JSON export failed:', error);
    }
    setExportLoading('');
  };

  const handleExportToPDF = async () => {
    setExportLoading('pdf');
    try {
      // Ensure we use the correct entity name from config
      const exportEntityName = config.entityName || 'Data';
      await exportToPDF(sortedData, tableColumns, exportEntityName, exportFileName);
    } catch (error) {
      console.error('PDF export failed:', error);
    }
    setExportLoading('');
  };

  const handleExportToExcel = async () => {
    setExportLoading('excel');
    try {
      // Ensure we use the correct entity name from config
      // Use config.entityName directly to avoid any issues with destructured value
      const exportEntityName = config.entityName || 'Data';
      await exportToExcel(sortedData, tableColumns, exportEntityName, exportFileName);
      addFormNotification('Excel file exported successfully!', 'success');
    } catch (error) {
      console.error('Excel export failed:', error);
      addFormNotification(`Excel export failed: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setExportLoading('');
    }
  };


  return (
    <div className="h-full flex flex-col space-y-6">
      {/* Data Table */}
      {!hideListWhenFormOpen || !isFormOpen ? (
      <Card className="flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden">
        {/* Header */}
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            {(() => {
              const filterSection = FilterSection({
                config,
                entityName,
                fields,
                data,
                labels,
                filtersVisible,
                setFiltersVisible,
                unifiedFilters,
                setUnifiedFilters,
                activeFilterTab,
                setActiveFilterTab,
                savedFilterPresets,
                setSavedFilterPresets,
                showFilterPresets,
                setShowFilterPresets,
                onApplyFilterPreset: handleApplyFilterPreset,
                onRefreshFilterPresets: refreshFilterPresets,
                onClearAllFilters: clearAllFilters,
                searchTerm,
                setSearchTerm
              });
              return filterSection.topControls;
            })()}

            {/* Right side buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
            {/* Table Presets */}
              <div className="relative">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setShowTablePresets(!showTablePresets)}
                  className="h-10 w-10"
                  title={labels.tablePresetsTooltip}
                >
                  <CogIcon className="w-4 h-4" />
                </Button>
                
                <TablePresets
                  visibleColumns={visibleColumns}
                  onApplyVisibleColumns={handleApplyVisibleColumns}
                  tableDensity={tableDensity}
                  onApplyDensity={handleDensityChange}
                  columnWidths={tableConfig.columnWidths}
                  columnOrder={tableConfig.columnOrder}
                  sortConfig={tableConfig.sortConfig}
                  onApplyTableConfig={handleApplyTableConfig}
                  tableId={String(config.ui?.tableStorageKey || entityName || 'default').toLowerCase()}
                  entityName={entityName}
                  isOpen={showTablePresets}
                  onClose={() => setShowTablePresets(false)}
                />
              </div>

              {/* Print Report Dropdown - For complaints */}
              {config.entityName?.toLowerCase() === 'complaint' && (
                <Dropdown
                  trigger={
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={!selectedRecord?.id}
                      title={selectedRecord?.id ? "Export Complaint Report to PDF" : "Select a complaint to export"}
                    >
                      <PrinterIcon className="w-5 h-5" />
                    </Button>
                  }
                  align="right"
                  className="w-64"
                >
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700">
                    Print Options
                  </div>
                  <DropdownItem 
                    onClick={async () => {
                      if (selectedRecord?.id) {
                        await handlePrintComplaint(selectedRecord.id, false);
                      } else {
                        addFormNotification('Please select a complaint to export', 'warning');
                      }
                    }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex-shrink-0">
                      <DocumentTextIcon className="w-5 h-5" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Print Report</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">PDF document only</span>
                    </div>
                  </DropdownItem>
                  <DropdownItem 
                    onClick={async () => {
                      if (selectedRecord?.id) {
                        await handlePrintComplaint(selectedRecord.id, true);
                      } else {
                        addFormNotification('Please select a complaint to export', 'warning');
                      }
                    }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex-shrink-0">
                      <PaperClipIcon className="w-5 h-5" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Print Report with Attachments</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">PDF + all files in ZIP</span>
                    </div>
                  </DropdownItem>
                </Dropdown>
              )}

              {/* Print Report Dropdown - For Product Status Change */}
              {config.entityName?.toLowerCase() === 'productstatuschange' && (
                <Dropdown
                  trigger={
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={!selectedRecord?.id}
                      title={selectedRecord?.id ? "Export Product Status Change Report to PDF" : "Select a product status change to export"}
                    >
                      <PrinterIcon className="w-5 h-5" />
                    </Button>
                  }
                  align="right"
                  className="w-64"
                >
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700">
                    Print Options
                  </div>
                  <DropdownItem 
                    onClick={async () => {
                      if (selectedRecord?.id) {
                        await handleExportProductStatusChangeToPdf(selectedRecord.id, false);
                      } else {
                        addFormNotification('Please select a product status change to export', 'warning');
                      }
                    }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex-shrink-0">
                      <DocumentTextIcon className="w-5 h-5" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Export PDF</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">PDF document only</span>
                    </div>
                  </DropdownItem>
                  {/*
                  <DropdownItem 
                    onClick={() => {
                      if (selectedRecord?.id) {
                        navigate(`/product-status-change/${selectedRecord.id}/print`);
                      } else {
                        addFormNotification('Please select a product status change to print', 'warning');
                      }
                    }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex-shrink-0">
                      <PrinterIcon className="w-5 h-5" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Print Report</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Open print preview in new tab</span>
                    </div>
                  </DropdownItem>
                  */}
                </Dropdown>
              )}

              {/* Print Report Dropdown - For Returned Product Inspection */}
              {config.entityName?.toLowerCase() === 'returnedproductinspection' && (
                <Dropdown
                  trigger={
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={!selectedRecord?.id}
                      title={
                        selectedRecord?.id
                          ? 'Print Returned Product Inspection Report'
                          : 'Select a returned product inspection to print'
                      }
                    >
                      <PrinterIcon className="w-5 h-5" />
                    </Button>
                  }
                  align="right"
                  className="w-64"
                >
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700">
                    Print Options
                  </div>
                  <DropdownItem
                    onClick={async () => {
                      if (selectedRecord?.id) {
                        await handleExportReturnedProductInspectionToPdf(
                          selectedRecord.id,
                          false,
                        );
                      } else {
                        addFormNotification(
                          'Please select a returned product inspection to print',
                          'warning',
                        );
                      }
                    }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex-shrink-0">
                      <DocumentTextIcon className="w-5 h-5" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Print Report
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        PDF document only
                      </span>
                    </div>
                  </DropdownItem>
                  <DropdownItem
                    onClick={async () => {
                      if (selectedRecord?.id) {
                        await handleExportReturnedProductInspectionToPdf(
                          selectedRecord.id,
                          true,
                        );
                      } else {
                        addFormNotification(
                          'Please select a returned product inspection to print',
                          'warning',
                        );
                      }
                    }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex-shrink-0">
                      <PaperClipIcon className="w-5 h-5" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Print Report with Attachments
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        PDF + all files in ZIP
                      </span>
                    </div>
                  </DropdownItem>
                </Dropdown>
              )}

              {config.entityName?.toLowerCase() === 'deviation' && (
                <Dropdown
                  trigger={
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={!selectedRecord?.id}
                      title={selectedRecord?.id ? "Export Deviation Report to PDF" : "Select a deviation to export"}
                    >
                      <PrinterIcon className="w-5 h-5" />
                    </Button>
                  }
                  align="right"
                  className="w-64"
                >
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700">
                    Print Options
                  </div>
                  <DropdownItem 
                    onClick={async () => {
                      if (selectedRecord?.id) {
                        await handlePrintDeviation(selectedRecord.id, false);
                      } else {
                        addFormNotification('Please select a deviation to export', 'warning');
                      }
                    }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex-shrink-0">
                      <DocumentTextIcon className="w-5 h-5" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Print Report</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">PDF document only</span>
                    </div>
                  </DropdownItem>
                  <DropdownItem 
                    onClick={async () => {
                      if (selectedRecord?.id) {
                        await handlePrintDeviation(selectedRecord.id, true);
                      } else {
                        addFormNotification('Please select a deviation to export', 'warning');
                      }
                    }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex-shrink-0">
                      <PaperClipIcon className="w-5 h-5" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Print Report with Attachments</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">PDF + all files in ZIP</span>
                    </div>
                  </DropdownItem>
                </Dropdown>
              )}
              {(config.entityName?.toLowerCase() === 'capaeffectivenesscheck') && (
                <Dropdown
                  trigger={
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={!selectedRecord?.id}
                      title={
                        selectedRecord?.id
                          ? 'Export CAPA Effectiveness Report to PDF'
                          : 'Select a CAPA effectiveness check to export'
                      }
                    >
                      <PrinterIcon className="w-5 h-5" />
                    </Button>
                  }
                  align="right"
                  className="w-64"
                >
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700">
                    Print Options
                  </div>
                  <DropdownItem
                    onClick={async () => {
                      if (selectedRecord?.id) {
                        await handlePrintCapaEffectiveness(selectedRecord.id, false);
                      } else {
                        addFormNotification(
                          'Please select a CAPA effectiveness check to export',
                          'warning',
                        );
                      }
                    }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex-shrink-0">
                      <DocumentTextIcon className="w-5 h-5" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Print Report
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        PDF document only
                      </span>
                    </div>
                  </DropdownItem>
                  <DropdownItem
                    onClick={async () => {
                      if (selectedRecord?.id) {
                        await handlePrintCapaEffectiveness(selectedRecord.id, true);
                      } else {
                        addFormNotification(
                          'Please select a CAPA effectiveness check to export',
                          'warning',
                        );
                      }
                    }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex-shrink-0">
                      <PaperClipIcon className="w-5 h-5" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Print Report with Attachments
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        PDF + all files in ZIP
                      </span>
                    </div>
                  </DropdownItem>
                </Dropdown>
              )}
              {config.entityName?.toLowerCase() === 'riskassessment' && (
                <Dropdown
                  trigger={
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={!selectedRecord?.id}
                      title={selectedRecord?.id ? "Print Risk Assessment Report" : "Select a risk assessment to print"}
                    >
                      <PrinterIcon className="w-5 h-5" />
                    </Button>
                  }
                  align="right"
                  className="w-64"
                >
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700">
                    Print Options
                  </div>
                  <DropdownItem
                    onClick={async () => {
                      if (selectedRecord?.id) {
                        await handlePrintRiskAssessment(selectedRecord.id, false);
                      } else {
                        addFormNotification('Please select a risk assessment to print', 'warning');
                      }
                    }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex-shrink-0">
                      <DocumentTextIcon className="w-5 h-5" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Print Report</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">PDF document only</span>
                    </div>
                  </DropdownItem>
                  <DropdownItem
                    onClick={async () => {
                      if (selectedRecord?.id) {
                        await handlePrintRiskAssessment(selectedRecord.id, true);
                      } else {
                        addFormNotification('Please select a risk assessment to print', 'warning');
                      }
                    }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex-shrink-0">
                      <PaperClipIcon className="w-5 h-5" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Print Report with Attachments</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">PDF + all files in ZIP</span>
                    </div>
                  </DropdownItem>
                  <DropdownItem
                    onClick={async () => {
                      if (selectedRecord?.id) {
                        await handleExportRiskAssessmentToExcel(selectedRecord.id);
                      } else {
                        addFormNotification('Please select a risk assessment to export', 'warning');
                      }
                    }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex-shrink-0">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Export to Excel</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Microsoft Excel format</span>
                    </div>
                  </DropdownItem>
                </Dropdown>
              )}

              {/* Print Report Dropdown - For Change Request */}
              {config.entityName?.toLowerCase() === 'changecontrol' && (
                <Dropdown
                  trigger={
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={!selectedRecord?.id}
                      title={
                        selectedRecord?.id
                          ? 'Print Change Request Report'
                          : 'Select a change request to print'
                      }
                    >
                      <PrinterIcon className="w-5 h-5" />
                    </Button>
                  }
                  align="right"
                  className="w-64"
                >
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700">
                    Print Options
                  </div>
                  <DropdownItem
                    onClick={async () => {
                      if (selectedRecord?.id) {
                        await handlePrintChangeRequest(selectedRecord.id, false);
                      } else {
                        addFormNotification(
                          'Please select a change request to print',
                          'warning',
                        );
                      }
                    }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex-shrink-0">
                      <DocumentTextIcon className="w-5 h-5" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Print Report
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        PDF document only
                      </span>
                    </div>
                  </DropdownItem>
                  <DropdownItem
                    onClick={async () => {
                      if (selectedRecord?.id) {
                        await handlePrintChangeRequest(selectedRecord.id, true);
                      } else {
                        addFormNotification(
                          'Please select a change request to print',
                          'warning',
                        );
                      }
                    }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex-shrink-0">
                      <PaperClipIcon className="w-5 h-5" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Print Report with Attachments
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        PDF + all files in ZIP
                      </span>
                    </div>
                  </DropdownItem>
                </Dropdown>
              )}

              {/* Export Dropdown */}
              <Dropdown
                trigger={
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={exportLoading !== ''}
                    title={labels.exportTooltip}
                  >
                    {exportLoading ? (
                      <SpinnerIcon className="w-4 h-4" />
                    ) : (
                      <DownloadIcon className="w-5 h-5" />
                    )}
                  </Button>
                }
                align="right"
                className="w-64"
              >
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700">
                  Export Options
                </div>
                <DropdownItem 
                  onClick={handleExportToPDF} 
                  disabled={exportLoading !== ''}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex-shrink-0">
                    <DocumentTextIcon className="w-5 h-5" />
                  </div>
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{labels.exportPdf}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Portable Document Format</span>
                  </div>
                </DropdownItem>
                <DropdownItem 
                  onClick={handleExportToExcel} 
                  disabled={exportLoading !== ''}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
                >
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex-shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{labels.exportExcel}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Microsoft Excel format</span>
                  </div>
                </DropdownItem>
              </Dropdown>

              {/* Add Button */}
              {config.ui?.showAddButton !== false && (
                <Button 
                  onClick={handleAdd} 
                  variant="default"
                  size="icon"
                  title={labels.addTooltip}
                >
                  <PlusIcon className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        {/* Filter Panels - Between header and table */}
        <div className="px-3 pb-0 pt-0">
        {(() => {
          const filterSection = FilterSection({
            config,
            entityName,
            fields,
            data,
            labels,
            filtersVisible,
            setFiltersVisible,
            unifiedFilters,
            setUnifiedFilters,
            activeFilterTab,
            setActiveFilterTab,
            savedFilterPresets,
            setSavedFilterPresets,
            showFilterPresets,
            setShowFilterPresets,
            onApplyFilterPreset: handleApplyFilterPreset,
            onRefreshFilterPresets: refreshFilterPresets,
            onClearAllFilters: clearAllFilters,
            searchTerm,
            setSearchTerm
          });
          return filterSection.filterPanels;
        })()}
        </div>

        {/* Table Section with Split View */}
        <div className="flex-1 min-h-0 flex gap-3">
          <TableSection
            tableVariant={tableVariant}
            tableConfig={tableConfig}
            tableDensity={tableDensity}
                        entityName={entityName}
            currentPageData={currentPageData}
            getVisibleColumns={getVisibleColumns}
            config={{
              ...config,
              customHandlers: {
                ...config.customHandlers,
                onViewHtml: config.customHandlers?.onViewHtml 
                  ? (record) => {
                      setHtmlPreviewRecord(record);
                    }
                  : undefined
              }
            }}
            isSplitViewOpen={isSplitViewOpen}
            boardLookupState={boardLookupState}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onFileCountClick={handleFileCountClick}
            onTodoTaskCountClick={handleTodoTaskCountClick}
            onTableConfigChange={handleTableConfigChange}
            onSplitViewSelect={handleSplitViewSelect}
            labels={labels}
          />
          
          {/* Split View Panel */}
          <SplitViewComponent
            isOpen={config.ui?.splitView && isSplitViewOpen}
            selectedRecord={selectedRecord}
            selectedRecordFiles={selectedRecordFiles}
            config={config}
            fields={fields}
            labels={labels}
            onClose={() => setIsSplitViewOpen(false)}
            onEdit={handleEdit}
            onView={handleView}
            onRecordUpdate={(updatedRecord) => {
              // Update the selected record with the latest data
              if (updatedRecord && updatedRecord.id === selectedRecord?.id) {
                setSelectedRecord(updatedRecord);
              }
            }}
          />
        </div>

        {/* Pagination Footer */}
        <CardFooter className="pt-4 pb-4">
          <div className="flex flex-col sm:flex-row items-center justify-between w-full gap-4">
            {/* Results Info */}
            <div className={`text-sm ${theme.text.muted} flex items-center gap-4`}>
              <span>
                {labels.showingText} {startIndex + 1}-{Math.min(endIndex, totalItems)} {labels.ofText} {totalItems} {entityName.toLowerCase()}s
                {Object.keys(unifiedFilters).length > 0 && (
                  <span className="ml-2 text-blue-600">
                    ({Object.keys(unifiedFilters).length} {Object.keys(unifiedFilters).length !== 1 ? labels.filtersText : labels.filterText} active)
                  </span>
                )}
              </span>
              
              {/* Page Size Selector */}
              <div className="flex items-center gap-2">
                <span className={`text-sm ${theme.text.muted}`}>{labels.showLabel}</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className={`px-2 py-1 text-sm border rounded ${theme.colors.surface} ${theme.text.primary} ${theme.border.light} focus:${theme.border.light}`}
                >
                  {(config.ui?.pageSizeOptions || [5, 10, 25, 50, 100]).map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
        </div>
      </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="icon"
                  title={labels.previousPageTooltip}
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                </Button>

                <div className="flex items-center gap-1">
                  {(() => {
                    const pages = [];
                    const maxVisible = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                    
                    if (endPage - startPage + 1 < maxVisible) {
                      startPage = Math.max(1, endPage - maxVisible + 1);
                    }

                    if (startPage > 1) {
                      pages.push(
                        <button
                          key={1}
                          onClick={() => handlePageChange(1)}
                          className="w-8 h-8 p-0 text-sm"
                        >
                          1
                </button>
                      );
                      if (startPage > 2) {
                        pages.push(<span key="start-ellipsis" className="px-2">...</span>);
                      }
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          className={`w-8 h-8 p-0 text-sm ${i === currentPage ? 'bg-blue-600 text-white' : 'border'}`}
                        >
                          {i}
                        </button>
                      );
                    }

                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(<span key="end-ellipsis" className="px-2">...</span>);
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          onClick={() => handlePageChange(totalPages)}
                          className="w-8 h-8 p-0 text-sm"
                        >
                          {totalPages}
                        </button>
                      );
                    }

                    return pages;
                  })()}
              </div>

                <Button
                  onClick={() => handleNextPage(totalPages)}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  size="icon"
                  title={labels.nextPageTooltip}
                >
                  <ArrowRightIcon className="w-4 h-4" />
                </Button>
            </div>
            )}
            </div>
        </CardFooter>
      </Card>
      ) : null}

      {/* Form Drawer or Custom Form */}
      {CustomFormComponent ? (
        <CustomFormComponent
          isOpen={isFormOpen} 
          formMode={formMode}
          formData={formData}
          formErrors={formErrors}
          isLoading={isLoading}
          isFormValid={isFormValid}
          formNotifications={formNotifications}
          entityName={entityName}
          config={config}
          fields={fields}
          labels={labels}
          selectedRecord={selectedRecord}
          setSelectedRecord={setSelectedRecord}
          onClose={closeFormAndNotify}
          onSubmit={handleFormSubmit}
          setFormData={setFormData}
          setFormErrors={setFormErrors}
          setFilesToDelete={setFilesToDelete}
          validateField={validateField}
          clearFormNotifications={clearFormNotifications}
          setFormNotifications={setFormNotifications}
        />
      ) : (
        <FormDrawer
          isOpen={isFormOpen} 
          formMode={formMode}
          formData={formData}
          formErrors={formErrors}
          isLoading={isLoading}
          isFormValid={isFormValid}
          formNotifications={formNotifications}
          entityName={entityName}
          selectedRecord={selectedRecord}
          config={config}
          fields={fields}
          labels={labels}
          onClose={closeFormAndNotify}
          onSubmit={handleFormSubmit}
          setFormData={setFormData}
          setFormErrors={setFormErrors}
          setFilesToDelete={setFilesToDelete}
          validateField={validateField}
          clearFormNotifications={clearFormNotifications}
          setFormNotifications={setFormNotifications}
        />
      )}

      {/* View Modal */}
      <ViewModal
        isOpen={isViewModalOpen}
        selectedRecord={selectedRecord}
        selectedRecordFiles={selectedRecordFiles}
        config={config}
        fields={fields}
        labels={labels}
        onClose={() => setIsViewModalOpen(false)}
      />

      {/* Delete Modal */}
      <DeleteModal
        isOpen={isDeleteModalOpen}
        selectedRecord={selectedRecord}
        isLoading={isLoading}
        entityName={entityName}
        tableColumns={tableColumns}
        labels={labels}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      {/* Duplicate Confirm Modal */}
      <DuplicateConfirmModal
        isOpen={isDuplicateModalOpen}
        selectedRecord={selectedRecordForDuplicate}
        isLoading={isLoading}
        entityName={entityName}
        tableColumns={tableColumns}
        labels={labels}
        onClose={() => {
          setIsDuplicateModalOpen(false);
          setSelectedRecordForDuplicate(null);
        }}
        onConfirm={handleDuplicateConfirm}
      />

      {/* File List Modal */}
      {fileListModalOpen && (
        <FileListModal
          isOpen={fileListModalOpen}
          onClose={() => setFileListModalOpen(false)}
          files={selectedRecordFiles}
          title={selectedRecordTitle}
        />
      )}

      <Modal isOpen={todoTasksModalOpen} onClose={() => setTodoTasksModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setTodoTasksModalOpen(false)}>
          <ModalTitle>{`Tasks - ${selectedTodoTaskTitle}`}</ModalTitle>
        </ModalHeader>
        <ModalContent maxHeight="60vh">
          {selectedTodoTasks.length === 0 ? (
            <p className={`text-sm ${theme.text.muted}`}>No tasks found.</p>
          ) : (
            <div className="space-y-2">
              {selectedTodoTasks.map((task, index) => {
                const taskText = String(task?.task ?? task?.Task ?? '').trim();
                const isDone = Boolean(task?.isDone ?? task?.IsDone);
                return (
                  <div
                    key={`${taskText}-${index}`}
                    className={`rounded-md border px-3 py-2 ${theme.border.light}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className={`m-0 text-sm ${isDone ? 'line-through opacity-70' : theme.text.primary}`}>
                        {taskText || '—'}
                      </p>
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          isDone
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                        }`}
                      >
                        {isDone ? 'Done' : 'Open'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={() => setTodoTasksModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* HTML Preview Modal */}
      {htmlPreviewRecord && (
        <Modal isOpen={!!htmlPreviewRecord} onClose={() => setHtmlPreviewRecord(null)} size="xl">
          <ModalHeader onClose={() => setHtmlPreviewRecord(null)}>
            <ModalTitle>{htmlPreviewRecord.templateSubject || 'Email Template Preview'}</ModalTitle>
          </ModalHeader>
          <ModalContent maxHeight="80vh">
            <div className="border rounded-md overflow-hidden bg-white">
              <iframe
                title="Email HTML Preview"
                sandbox="allow-same-origin"
                style={{ width: '100%', height: '70vh', border: 'none' }}
                srcDoc={htmlPreviewRecord.templateBody}
              />
            </div>
          </ModalContent>
          <ModalFooter>
            <Button onClick={() => setHtmlPreviewRecord(null)} variant="secondary">
              Close
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Alert Dialog */}
      <AlertDialog
        isOpen={alertState.show}
        onClose={() => setAlertState({ show: false, message: '', type: 'error' })}
        title=""
        message={alertState.message}
        type={alertState.type}
        confirmText="OK"
      />

    </div>
  );
};

export default GenericFormsPage;
